import { useQueries } from "@tanstack/react-query";

import { optionCountParams } from "../option-count-params";
import { useOptionCountsContext } from "../option-counts";
import type { FilterField, FilterOption } from "../types";
import { useRouteListSearch } from "./use-route-list-search";

/**
 * `options` with each one's row count filled in, read while `enabled` (the
 * popover is open): one count read per option, under the list's other filters
 * and search. Returns `options` untouched, and reads nothing, unless the field
 * sets `countOptions` and an `OptionCountsProvider` is above it. An option's
 * `count` stays unset until its read answers.
 */
export function useOptionCounts(
  field: FilterField<string, any, any>,
  options: FilterOption[],
  { enabled }: { enabled: boolean },
): FilterOption[] {
  const counting = useOptionCountsContext();
  const search = useRouteListSearch();
  const counted = field.countOptions && counting !== null;

  const results = useQueries({
    queries: counted
      ? options.map((option) => {
          const query = counting.countQueryOf(
            optionCountParams({
              fields: counting.fields,
              field,
              search,
              value: option.value,
            }),
          );
          return { ...query, enabled: enabled && (query.enabled ?? true) };
        })
      : [],
  });

  if (!counted) return options;
  return options.map((option, index) => {
    const count = results[index]?.data;
    return count === undefined ? option : { ...option, count };
  });
}
